import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  Smartphone, 
  Monitor, 
  Tablet, 
  Clock, 
  MapPin, 
  LogOut, 
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Trash2,
  Globe
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { sessionsAPI } from '../services/api';

// Device icon component
const DeviceIcon = ({ type, className = "w-6 h-6" }) => {
  switch (type?.toLowerCase()) {
    case 'mobile':
      return <Smartphone className={className} />;
    case 'tablet':
      return <Tablet className={className} />;
    case 'desktop':
    default:
      return <Monitor className={className} />;
  }
};

// Format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Unknown';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

// Session Card Component
const SessionCard = ({ session, onLogout, isLoggingOut }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  
  return (
    <div 
      data-testid={`session-card-${session.id}`}
      className={`p-3 sm:p-4 border rounded-lg ${session.isCurrent ? 'border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-200 dark:border-border bg-white dark:bg-card'} transition-all hover:shadow-md`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        {/* Device Info */}
        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
          <div className={`p-1.5 sm:p-2 rounded-lg shrink-0 ${session.isCurrent ? 'bg-teal-100 dark:bg-teal-900/30' : 'bg-gray-100 dark:bg-secondary'}`}>
            <DeviceIcon type={session.deviceType} className={`w-5 h-5 sm:w-6 sm:h-6 ${session.isCurrent ? 'text-teal-600' : 'text-gray-600 dark:text-muted-foreground'}`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-gray-900 dark:text-foreground text-sm sm:text-base truncate">{session.deviceName}</h4>
              {session.isCurrent && (
                <Badge className="bg-teal-500 text-white text-[10px] sm:text-xs shrink-0">Current</Badge>
              )}
            </div>
            
            <div className="mt-1 space-y-0.5">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground flex items-center gap-1.5">
                <Globe className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 dark:text-muted-foreground shrink-0" />
                <span className="truncate">{session.browser} • {session.os}</span>
              </p>
              
              <p className="text-xs sm:text-sm text-gray-500 dark:text-muted-foreground flex items-center gap-1.5">
                <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 dark:text-muted-foreground shrink-0" />
                <span className="truncate">IP: {session.ipAddress}</span>
              </p>
              
              <p className="text-xs sm:text-sm text-gray-500 dark:text-muted-foreground flex items-center gap-1.5">
                <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 dark:text-muted-foreground shrink-0" />
                <span>Last active: {formatRelativeTime(session.lastActive)}</span>
              </p>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="shrink-0 self-end sm:self-start">
          {session.isCurrent ? (
            <Badge variant="outline" className="text-teal-600 border-teal-300 text-xs">
              <CheckCircle className="w-3 h-3 mr-1" />
              Active
            </Badge>
          ) : (
            <>
              {!showConfirm ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowConfirm(true)}
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 text-xs sm:text-sm h-8"
                  disabled={isLoggingOut}
                >
                  <LogOut className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Logout
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowConfirm(false)}
                    disabled={isLoggingOut}
                    className="text-xs h-8 px-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onLogout(session.id)}
                    disabled={isLoggingOut}
                    className="text-xs h-8 px-2"
                  >
                    {isLoggingOut ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Confirm'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Security = () => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  
  const [sessions, setSessions] = useState([]);
  const [maxSessions, setMaxSessions] = useState(2);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(null);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  
  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await sessionsAPI.getAll();
      setSessions(data.sessions || []);
      setMaxSessions(data.maxSessions || 2);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load active sessions',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);
  
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);
  
  // Logout from specific session
  const handleLogoutSession = async (sessionId) => {
    try {
      setLoggingOut(sessionId);
      await sessionsAPI.logout(sessionId);
      
      toast({
        title: 'Session Terminated',
        description: 'The device has been logged out successfully'
      });
      
      // Refresh sessions list
      await fetchSessions();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to logout session',
        variant: 'destructive'
      });
    } finally {
      setLoggingOut(null);
    }
  };
  
  // Logout from all other sessions
  const handleLogoutAllOthers = async () => {
    try {
      setLoggingOutAll(true);
      const result = await sessionsAPI.logoutAllOthers();
      
      toast({
        title: 'All Other Sessions Terminated',
        description: result.message || 'All other devices have been logged out'
      });
      
      // Refresh sessions list
      await fetchSessions();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to logout other sessions',
        variant: 'destructive'
      });
    } finally {
      setLoggingOutAll(false);
    }
  };
  
  const otherSessionsCount = sessions.filter(s => !s.isCurrent).length;
  
  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0" data-testid="security-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground flex items-center gap-2">
          <Shield className="w-7 h-7 text-teal-600" />
          Security
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-muted-foreground mt-1">
          Manage your active sessions and device access
        </p>
      </div>
      
      {/* Session Limit Info */}
      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
        <CardContent className="p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium text-amber-800 dark:text-amber-200 text-sm sm:text-base">Device Login Limit</p>
            <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-300">
              You can be logged in on up to <strong>{maxSessions} devices</strong> at a time. 
              When you login on a new device, the oldest session will be automatically logged out.
            </p>
          </div>
        </CardContent>
      </Card>
      
      {/* Active Sessions Card */}
      <Card className="border-0 shadow-md overflow-hidden">
        <CardHeader className="border-b dark:border-border bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 p-3 sm:p-6">
          <div className="space-y-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg dark:text-foreground">
                <Monitor className="w-5 h-5 text-teal-600" />
                Active Sessions
              </CardTitle>
              <CardDescription className="mt-1 text-xs sm:text-sm">
                {sessions.length} of {maxSessions} devices currently logged in
              </CardDescription>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchSessions}
                disabled={loading}
                className="text-xs sm:text-sm h-8"
              >
                <RefreshCw className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              {otherSessionsCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogoutAllOthers}
                  disabled={loggingOutAll}
                  className="text-red-600 border-red-200 hover:bg-red-50 text-xs sm:text-sm h-8"
                >
                  {loggingOutAll ? (
                    <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  )}
                  <span className="hidden sm:inline">Logout All Others</span>
                  <span className="sm:hidden">Logout All</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-teal-600" />
              <span className="ml-2 text-gray-600 dark:text-muted-foreground">Loading sessions...</span>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8">
              <Monitor className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-muted-foreground">No active sessions found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Sort to show current session first */}
              {sessions
                .sort((a, b) => (b.isCurrent ? 1 : 0) - (a.isCurrent ? 1 : 0))
                .map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    onLogout={handleLogoutSession}
                    isLoggingOut={loggingOut === session.id}
                  />
                ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Security Tips */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <CardTitle className="text-sm sm:text-base flex items-center gap-2 dark:text-foreground">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
            Security Tips
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-3 p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-200">
              <strong>Review regularly:</strong> Check your active sessions periodically and logout from devices you no longer use.
            </p>
          </div>
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-200">
              <strong>Don&apos;t recognize a device?</strong> If you see an unfamiliar session, logout from it immediately and change your password.
            </p>
          </div>
          <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
            <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-200">
              <strong>Shared devices:</strong> Always logout when using shared or public computers.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Security;
